/**
 * Cross-Tab Synchronization Utility using BroadcastChannel API
 * Handles communication between multiple tabs of the WABA application
 */

class CrossTabSync {
    constructor() {
        this.listeners = new Map();
        this.channelName = 'waba-cross-tab-sync';
        
        // Check if BroadcastChannel is supported
        if ('BroadcastChannel' in window) {
            this.channel = new BroadcastChannel(this.channelName);
            this.channel.onmessage = (event) => {
                this.handleMessage(event.data);
            };
            this.method = 'broadcast';
        } else {
           // console.warn('BroadcastChannel not supported, falling back to localStorage');
            // Fallback to localStorage events for older browsers
            window.addEventListener('storage', (event) => {
                if (event.key?.startsWith('waba-sync-')) {
                    try {
                        const data = JSON.parse(event.newValue);
                        this.handleMessage(data);
                    } catch (error) {
                        //console.error(ErrorExtractor(error))
                    }
                }
            });
            this.method = 'storage';
        }
    }

    /**
     * Send a message to all other tabs
     * @param {string} type - Message type
     * @param {any} data - Message data
     */
    send(type, data = null) {
        const message = {
            type,
            data,
            timestamp: Date.now(),
            tabId: this.getTabId()
        };

        try {
            if (this.method === 'broadcast') {
                this.channel.postMessage(message);
            } else {
                // Fallback to localStorage
                const key = `waba-sync-${type}-${Date.now()}`;
                localStorage.setItem(key, JSON.stringify(message));
                // Clean up immediately to trigger storage event
                setTimeout(() => {
                    try {
                        localStorage.removeItem(key);
                    } catch (error) {
                        // Ignore cleanup errors
                    }
                }, 100);
            }
            // console.log(`Cross-tab message sent: ${type}`, data);
        } catch (error) {
            // console.error('Error sending cross-tab message:', error);
        }
    }

    /**
     * Listen for a specific message type
     * @param {string} type - Message type to listen for
     * @param {function} callback - Callback function to execute
     */
    on(type, callback) {
        if (!this.listeners.has(type)) {
            this.listeners.set(type, []);
        }
        this.listeners.get(type).push(callback);
    }

    /**
     * Remove a listener for a specific message type
     * @param {string} type - Message type
     * @param {function} callback - Callback function to remove
     */
    off(type, callback) {
        if (this.listeners.has(type)) {
            const callbacks = this.listeners.get(type);
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        }
    }

    /**
     * Handle incoming messages
     * @param {object} message - The received message
     */
    handleMessage(message) {
        const { type, data, timestamp, tabId } = message;
        
        // Ignore messages from the same tab
        if (tabId === this.getTabId()) {
            return;
        }

        // Execute callbacks for this message type
        const callbacks = this.listeners.get(type);
        if (callbacks) {
            callbacks.forEach(callback => {
                try {
                    callback(data, { timestamp, tabId });
                } catch (error) {
                  //  console.error(`Error executing callback for ${type}:`, error);
                }
            });
        }
    }

    /**
     * Get a unique identifier for this tab
     * @returns {string} Tab identifier
     */
    getTabId() {
        if (!this.tabId) {
            this.tabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        }
        return this.tabId;
    }

    /**
     * Close the broadcast channel
     */
    close() {
        if (this.channel) {
            this.channel.close();
        }
        this.listeners.clear();
    }

    /**
     * Predefined message types for WABA application
     */
    static MessageTypes = {
        USER_LOGOUT: 'USER_LOGOUT',
        USER_LOGIN: 'USER_LOGIN',
        BALANCE_UPDATE: 'BALANCE_UPDATE',
        IMPERSONATE_START: 'IMPERSONATE_START',
        IMPERSONATE_END: 'IMPERSONATE_END',
        TEMPLATE_UPDATE: 'TEMPLATE_UPDATE',
        USER_STATUS_CHANGE: 'USER_STATUS_CHANGE',
        MAINTENANCE_MODE: 'MAINTENANCE_MODE',
        AGENT_STATUS_CHANGE: 'AGENT_STATUS_CHANGE',
        TWO_FACTOR_UPDATE: 'TWO_FACTOR_UPDATE'
    };
}

// Create and export a singleton instance
export const crossTabSync = new CrossTabSync();

// Export the class for advanced usage
export { CrossTabSync };

// Export message types for convenience
export const { MessageTypes } = CrossTabSync;
